/** @jsxImportSource react */
import React, { useEffect, useMemo, useState, CSSProperties } from 'react';
import './LoadingScreen.scss';

interface LoadingScreenProps {
  progress?: number;
  isLoading: boolean;
  currentTask?: string;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ 
  progress = 0, 
  isLoading, 
  currentTask = 'Initializing...' 
}): JSX.Element | null => {
  const [tipIndex, setTipIndex] = useState<number>(0);
  const tips = useMemo<string[]>(
    () => [
      'Welcome to EzSalon! Loading your workspace...',
      'Tip: Use quick filters to find appointments faster',
      'Tip: Regular backups keep your data safe',
      "Tip: Check the dashboard for today's overview",
    ],
    []
  );

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (isLoading) {
      // Delay the tip rotation to avoid initial load impact
      interval = setTimeout(() => {
        interval = setInterval(() => {
          setTipIndex(prev => (prev + 1) % tips.length);
        }, 3000);
      }, 1000);
    }
    return () => {
      if (interval) {
        clearTimeout(interval);
        clearInterval(interval);
      }
    };
  }, [isLoading, tips]);

  if (!isLoading) return null;

  const progressBarStyle: CSSProperties = {
    width: `${progress}%`
  };

  return (
    <div className="loading-screen">
      <div className="loading-content">
        <div className="logo">ezSalon.io</div>
        <div className="progress-container">
          <div className="progress-bar" style={progressBarStyle} />
        </div>
        <div className="current-task">{currentTask}</div>
        <div className="loading-tip">{tips[tipIndex]}</div>
      </div>
    </div>
  );
};

export default LoadingScreen;
